import React, { useRef, useState, useEffect } from "react";
import { Volume2, Play, Pause, Square, Copy, Loader2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";

const SPEEDS = [0.75, 1.0, 1.25, 1.5, 2.0];

export default function ReadAloud({ screen, getContext, variant = "header" }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1.0);
  const [showTranscript, setShowTranscript] = useState(false);
  const audioRef = useRef(null);
  const startRef = useRef(0);

  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); } }, []);

  const logPlayback = async (status) => {
    const duration = startRef.current ? (Date.now() - startRef.current) / 1000 : 0;
    try {
      await api.post("/readaloud-logs", { screen, language: lang, duration, status, summary });
    } catch { /* ignore */ }
  };

  const speakFallback = (text) => {
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang === "ar" ? "ar-SA" : "en-US";
    u.rate = speed;
    u.onend = () => { setPlaying(false); logPlayback("completed"); };
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    setPlaying(true);
    startRef.current = Date.now();
  };

  const generateAndPlay = async () => {
    setOpen(true);
    setLoading(true);
    setSummary("");
    try {
      const ctx = getContext ? getContext() : {};
      const { data } = await api.post("/summarize", {
        screen, context: ctx, language: lang, role: user?.role || "admin",
      });
      const text = data.summary || "";
      setSummary(text);
      setLoading(false);
      try {
        const res = await api.post("/tts", { text, language: lang, speed }, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const audio = new Audio(url);
        audio.playbackRate = speed;
        audioRef.current = audio;
        audio.onended = () => { setPlaying(false); logPlayback("completed"); };
        audio.onplay = () => setPlaying(true);
        audio.onpause = () => setPlaying(false);
        startRef.current = Date.now();
        await audio.play();
      } catch {
        speakFallback(text);
      }
    } catch (e) {
      setLoading(false);
      toast.error("Could not generate summary");
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (audio) {
      if (audio.paused) audio.play(); else audio.pause();
    } else if (summary) {
      if (window.speechSynthesis?.speaking && !window.speechSynthesis.paused) {
        window.speechSynthesis.pause(); setPlaying(false);
      } else if (window.speechSynthesis?.paused) {
        window.speechSynthesis.resume(); setPlaying(true);
      } else {
        speakFallback(summary);
      }
    }
  };

  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setPlaying(false);
  };

  const changeSpeed = (s) => {
    setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  };

  const triggerCls = variant === "header"
    ? "inline-flex items-center gap-2 rounded-full bg-primary/10 hover:bg-primary/20 text-primary px-3.5 py-2 text-sm font-semibold transition-colors"
    : "inline-flex items-center justify-center h-8 w-8 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-colors";

  return (
    <>
      <button data-testid={`read-aloud-btn-${screen}`} onClick={generateAndPlay} className={triggerCls} title={t("read_aloud")}>
        <Volume2 className="h-4 w-4" />
        {variant === "header" && <span className="hidden sm:inline">{t("read_aloud")}</span>}
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) stop(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="read-aloud-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-start">
              <Volume2 className="h-5 w-5 text-primary" /> {t("voice_summary")}
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-center gap-1 h-16 my-2" aria-hidden>
            {[...Array(9)].map((_, i) => (
              <span key={i}
                className={`eq-bar w-1.5 rounded-full ${playing ? "bg-primary" : "bg-primary/25"}`}
                style={{ height: "48px", animationDelay: `${i * 0.08}s`, animationPlayState: playing ? "running" : "paused" }} />
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("generating_summary")}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2">
                <Button data-testid="read-aloud-playpause" onClick={togglePlay} size="icon" className="h-11 w-11 rounded-full">
                  {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                </Button>
                <Button data-testid="read-aloud-stop" onClick={stop} size="icon" variant="outline" className="h-11 w-11 rounded-full">
                  <Square className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center justify-center gap-1.5 flex-wrap">
                <span className="text-xs font-medium text-muted-foreground me-1">{t("speed")}</span>
                {SPEEDS.map((s) => (
                  <button key={s} data-testid={`speed-${s}`} onClick={() => changeSpeed(s)}
                    className={`rounded-md px-2 py-1 text-xs font-semibold transition-colors ${speed === s ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`}>
                    {s}x
                  </button>
                ))}
              </div>

              <div>
                <button data-testid="toggle-transcript" onClick={() => setShowTranscript((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                  <FileText className="h-4 w-4" /> {t("transcript")}
                </button>
                {showTranscript && (
                  <div className="mt-2 rounded-xl border bg-muted/40 p-4">
                    <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap" data-testid="transcript-text">{summary}</p>
                    <Button variant="ghost" size="sm" className="mt-2 gap-1.5"
                      onClick={() => { navigator.clipboard.writeText(summary); toast.success(t("copy")); }}>
                      <Copy className="h-3.5 w-3.5" /> {t("copy")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
